import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  addPermissionsToRole,
  getPermissions,
  getPermissionsForRole,
  newRole,
} from "../../../services/settings";
import Toggle from "../../../components/Toggle";
import { useAuth } from "../../../context/AuthContextProvider";
import BusinessName from "../../../components/BusinessName";

const AssignPermissions = ({
  handleAssign,
  handleCancel,
  roleId,
  secondRoleId,
  newRoleName,
  newRoleDesp,
}) => {
  const [permissions, setPermissions] = useState([]);
  const [activePermissionId, setActivePermissionId] = useState(null);
  const [clickedPermission, setClickedPermission] = useState(null);
  const [checkedPermissions, setCheckedPermissions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { globalProfile } = useAuth();

  const handlePermissions = useCallback(async () => {
    try {
      // Fetch all permissions
      const allPermissionsRes = await getPermissions();
      const allPermissions = mergePermissions(allPermissionsRes.data);

      // Fetch permissions for specific role
      let rolePermissions = [];
      if (secondRoleId) {
        const rolePermissionsRes = await getPermissionsForRole(secondRoleId);
        rolePermissions = mergePermissions(rolePermissionsRes.data);
      }

      // Extract IDs of permissions the user has
      const userPermissionIds = rolePermissions.flatMap((permissionType) =>
        permissionType.permissions.map((permission) => permission.id)
      );

      // Merge both sets of permissions and set checked state
      const mergedPermissions = allPermissions.map((permissionType) => {
        return {
          ...permissionType,
          permissions: permissionType.permissions.map((permission) => ({
            ...permission,
            checked: userPermissionIds.includes(permission.id),
          })),
        };
      });

      setPermissions(mergedPermissions);
      setCheckedPermissions(userPermissionIds); // Add user's permission IDs to checkedPermissions
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  }, [secondRoleId]);

  const mergePermissions = (data) => {
    if (!data || !Array.isArray(data)) return [];

    // Check if the backend response is already grouped by feature
    if (data.length > 0 && data[0].hasOwnProperty("feature") && Array.isArray(data[0].permissions)) {
      return data.map((item) => ({
        permissionType: item.feature,
        permissions: item.permissions.map((p) => ({ ...p, checked: false })),
      }));
    }

    const merged = data.reduce((acc, curr) => {
      const type = curr.permissionType || curr.feature || "General";
      const existingPermission = acc.find(
        (item) => item.permissionType === type
      );
      if (existingPermission) {
        existingPermission.permissions.push(curr);
      } else {
        acc.push({
          permissionType: type,
          permissions: [{ ...curr, checked: false }],
        });
      }
      return acc;
    }, []);
    return merged;
  };

  useEffect(() => {
    handlePermissions();
  }, [handlePermissions, globalProfile]);

  const toggleActivePermission = (id) => {
    setActivePermissionId(id === activePermissionId ? null : id);
    const clickedPermission = permissions.find(
      (permission, index) => index === id
    );
    setClickedPermission(clickedPermission);
    setIsModalOpen(true); // Open the modal on clicking a permission
  };

  const handleCheckboxChange = (e, permissionId) => {
    const isChecked = e.target.checked;

    setCheckedPermissions((prevChecked) => {
      if (isChecked) {
        return [...prevChecked, permissionId];
      } else {
        return prevChecked.filter((id) => id !== permissionId);
      }
    });
  };

  const handleRoles = async () => {
    try {
      if (newRoleName && newRoleDesp) {
        // Build permissionNames array from checkedPermissions IDs
        const selectedPermissionNames = [];
        permissions.forEach((category) => {
          category.permissions.forEach((p) => {
            if (checkedPermissions.includes(p.id)) {
              selectedPermissionNames.push(p.name);
            }
          });
        });

        const res = await newRole({
          name: newRoleName,
          description: newRoleDesp,
          permissionNames: selectedPermissionNames,
          roleType: 1,
        });
        toast.success(res.message);
        handleAssign();
      } else {
        const res = await addPermissionsToRole({
          permissionIds: checkedPermissions,
          roleId: roleId,
        });
        toast.success(res.message);
        handleAssign();
      }
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  const assignAll = (isChecked) => {
    if (isChecked) {
      // Add all permission IDs to checkedPermissions
      const allPermissionIds = permissions.flatMap((permission) =>
        permission.permissions.map((p) => p.id)
      );
      setCheckedPermissions(allPermissionIds);
    } else {
      // Clear checkedPermissions
      setCheckedPermissions([]);
    }
  };

  return (
    <div className="">
      <div className="flex sm:flex-row flex-col items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-blue">
          Settings - Permission
        </h1>
        <Toggle />
      </div>

      {/* <section className="flex justify-end text-xl font-bold pt-4 mb-4 pr-4">
        <span className="ml-2">
          <BusinessName />
        </span>
      </section> */}

      <div className="w-full max-w-72 md:max-w-full xl:w-[1250px] h-auto bg-white mt-8 rounded-[10px] md:p-[3rem]">
        <div className="border border-gray-200 rounded-t-[10px] p-[1rem] pl-7 flex justify-between items-center">
          <p className="text-primary text-xl font-semibold">
            Add user permission
          </p>
          <span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.5 6.75L9 11.25L13.5 6.75"
                stroke="#464255"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        <section className="mt-6 px-6">
          <div className="flex items-center gap-6">
            <span className="bg-primary flex justify-center items-center text-white w-[120px] h-[23px] rounded-[10px]">
              Step 2 of 2
            </span>
            <span className="text-xl">Assign permissions</span>
          </div>

          <article className="flex flex-col sm:flex-row mt-6">
            <section className="flex-[1]">
              <div className="flex flex-col gap-4 w-full">
                <div className="pb-6 border-b">
                  Define and assign the permissions and access for team members
                  with this role. You can make changes to this much later.
                </div>

                <div className="flex px-4">
                  <div className="mt-[2px] mr-1">
                    <label className="switches">
                      <input
                        type="checkbox"
                        onChange={(e) => assignAll(e.target.checked)}
                      />
                      <span className="sliders round"></span>
                    </label>
                  </div>
                  <span className="w-[428px] text-sm font-normal">
                    Assign all permissions for this role (except for transfer of
                    account ownership this can only be done by the account
                    owner)
                  </span>
                </div>
                {permissions.map((permission, index) => (
                  <div
                    key={index}
                    className={`p-4 cursor-pointer ${
                      index === activePermissionId ? "bg-gray-100" : "border-b"
                    }`}
                    onClick={() => toggleActivePermission(index)}
                  >
                    <span className="font-bold ">
                      {permission.permissionType}
                    </span>
                    {permission.permissions.map((permission) => (
                      <span key={permission.id}>
                        - {permission.description}{" "}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </section>

            {isModalOpen && activePermissionId !== null && (
              <div className="fixed inset-0  lg:relative flex items-center md:items-start justify-center xl:bg-white bg-black bg-opacity-50 z-50">
                <div className="bg-white mr-20  ml-4 sm:w-[480px] p-4 rounded-lg">
                  <article className=" border-2 p-4 md:w-[480px]">
                    <div className="pb-4 border-b">
                      {clickedPermission?.permissionType}
                    </div>

                    <div className="font-sm my-4">
                      Tick the boxes to select permissions you want to assign to
                      this role
                    </div>

                    {clickedPermission?.permissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="container"
                        style={{ fontSize: "14px", fontWeight: 400 }}
                      >
                        {permission.name}
                        <input
                          type="checkbox"
                          id={permission.name}
                          name={permission.name}
                          value={permission.id}
                          checked={checkedPermissions.includes(permission.id)}
                          onChange={(e) =>
                            handleCheckboxChange(e, permission.id)
                          }
                        />
                        <span className="checkmark"></span>
                      </label>
                    ))}
                  </article>

                  <div className="flex gap-4 justify-center mt-4">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="w-[100px] text-primary border border-primary p-2 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRoles}
                      className="w-[100px] bg-primary text-white p-2 rounded-md"
                    >
                      Assign
                    </button>
                  </div>
                </div>
              </div>
            )}
          </article>
        </section>
      </div>
      <style>
        {`
.switches {
  position: relative;
  display: inline-block;
  width: 33.12px;
  height: 17px;
}

.switches input { 
  opacity: 0;
  width: 0;
  height: 0;
}

.sliders {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EBEBEB;
  -webkit-transition: .4s;
  transition: .4s;
}

.sliders:before {
  position: absolute;
  content: "";
  height: 17px;
  width: 17px;
  left: 2px;
  
  background-color: #fff;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .sliders {
  background-color: var(--primary);
}

input:focus + .sliders {
  box-shadow: 0 0 1px #2196F3;
}

input:checked + .sliders:before {
  -webkit-transform: translateX(18px);
  -ms-transform: translateX(18px);
  transform: translateX(18px);
}

/* Rounded sliders */
.sliders.round {
  border-radius: 34px;
}

.sliders.round:before {
  border-radius: 50%;
}`}
      </style>
    </div>
  );
};

export default AssignPermissions;
